import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import type { DoseFormValues } from "@/components/dose/dose-form/dose-form-schema";
import { type EditDoseInventorySnapshot, findMedicationForEditComponent, getDoseInventoryMedicationId, getEditDoseFormValues, getOriginalComponentsById, toEditDosePayload } from "@/components/dose/dose-form/edit-dose-model";
import { queryClient } from "@/config/react-query";
import { useUser } from "@/contexts/user-provider";
import { toast } from "@/lib/use-toast";
import type { Medication, MedicationSummary } from "@/types/medication";
import { useUpdateMedicationMutation } from "@/api/provider/user/patient/patient-mutation";
import { type ShipmentLot, doseInventoryPreviewOptions, medicationsQueryOptions } from "@/query/medications/medications";
const EMPTY_MEDICATIONS: Medication[] = [];
export type DoseEdit = (values: DoseFormValues) => DoseFormValues;

/**
 * Saves one cell's change to a dose. The change is applied to the same form
 * values the Edit dose window opens with and sent through the same payload
 * builder, so lots, stock and location rows behave exactly as they do there.
 */
export function useInlineDoseEdit() {
  const $ = _c(10);
  const updateMedication = useUpdateMedicationMutation();
  const {
    currentUser,
    currentLocationId
  } = useUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = medicationsQueryOptions.list();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: t1
  } = useQuery(t0);
  const medications = t1 === undefined ? EMPTY_MEDICATIONS : t1;
  let t2;
  if ($[1] !== currentLocationId || $[2] !== currentUser?.id || $[3] !== medications || $[4] !== updateMedication) {
    t2 = async (dose, edit, t3) => {
      const {
        editLocations: t4
      } = t3 === undefined ? {} : t3;
      const editLocations = t4 === undefined ? false : t4;
      const values = edit({
        ...getEditDoseFormValues(dose),
        doseDate: dose.doseDate ? new Date(dose.doseDate) : undefined
      });
      const originals = getOriginalComponentsById(dose);
      const inventory = await Promise.all(values.components.map(async component => {
        const original = component.id ? originals.get(component.id) : undefined;
        const medication = findMedicationForEditComponent(component, medications);
        const medicationId = getDoseInventoryMedicationId(component, medication);
        const needsShipments = component.lotAllocations !== undefined || !!original?.stockId || !!original?.lots?.some(_temp) || !!original?.inventoryLots?.some(_temp2) || component.amount !== original?.amount || component.quantity !== original?.quantity || component.unit !== original?.unit || (component.dilutionId ?? null) !== (original?.dilutionId ?? null);
        if (!medicationId || !needsShipments) {
          return {
            medicationId,
            shipments: []
          };
        }
        const shipments = await queryClient.fetchQuery(medicationsQueryOptions.availableLots(medicationId, (dose.locationId ?? currentLocationId) || undefined, dose.provider?.id || currentUser?.id, component.id, undefined, dose.patientId)).catch(_temp3);
        const locationId = (dose.locationId ?? currentLocationId) || undefined;
        const providerId = dose.provider?.id || currentUser?.id;
        const preview = dose.patientId ? await queryClient.fetchQuery(doseInventoryPreviewOptions(medicationId, {
          patientId: dose.patientId,
          providerId,
          locationId,
          doseComponentId: component.id,
          compoundMedicationId: values.compoundMedicationId ?? null,
          amount: component.amount,
          unit: component.unit,
          quantity: component.quantity,
          dilutionId: component.dilutionId
        })).catch(_temp4) : undefined;
        return {
          medicationId,
          shipments: shipments ?? [],
          selectedMedication: medication,
          locationId,
          providerId,
          compoundMedicationId: values.compoundMedicationId ?? null,
          preview
        };
      }));
      let update;
      ;
      try {
        update = toEditDosePayload(values, {
          medication: dose,
          medications,
          inventory,
          canChangeProvider: false,
          editLocations
        });
      } catch (t5) {
        const error = t5;
        toast({
          title: "Failed to update medication",
          description: error instanceof Error ? error.message : "Failed to update medication",
          variant: "error"
        });
        throw error;
      }
      await updateMedication.mutateAsync({
        medicationId: dose.id,
        update
      });
    };
    $[1] = currentLocationId;
    $[2] = currentUser?.id;
    $[3] = medications;
    $[4] = updateMedication;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  currentUser?.id;
  const save = t2;
  let t3;
  if ($[6] !== medications || $[7] !== save || $[8] !== updateMedication.isPending) {
    t3 = {
      save,
      medications,
      isSaving: updateMedication.isPending
    };
    $[6] = medications;
    $[7] = save;
    $[8] = updateMedication.isPending;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function _temp4() {}
function _temp3() {
  return [] as ShipmentLot[];
}
function _temp2(lot) {
  return lot.quantity > 0;
}
function _temp(lot_0) {
  return lot_0.shipmentId;
}